import React, { useState, useEffect } from "react";
import { getFullHomePageContent, updateHomePageContent } from "../../../../firebase/settings-functions.js";
import { uploadFile } from "../../../../firebase/firebase";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";

const HomePageTab = ({ storeId }) => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);
    const [homePageData, setHomePageData] = useState({
        heroSection: {
            en: {
                title: "",
                subtitle: "",
            },
            ar: {
                title: "",
                subtitle: "",
            },
            mediaType: "video",
            mediaUrl: "",
            overlayOpacity: 0.5
        },
        storySection: {
            en: {
                title: "",
                description: "",
                buttonText: "DISCOVER MORE",
            },
            ar: {
                title: "",
                description: "",
                buttonText: "اكتشف المزيد",
            },
            buttonLink: "/shop"
        },
        aboutUsSection: {
            imageUrl: ""
        }
    });

    const [activeLanguage, setActiveLanguage] = useState('en');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState("");
    const [aboutUsImageFile, setAboutUsImageFile] = useState(null);
    const [aboutUsImagePreview, setAboutUsImagePreview] = useState("");

    // Fetch existing home page content
    useEffect(() => {
        const fetchHomePageContent = async () => {
            if (!storeId) return;

            try {
                setLoading(true);
                const content = await getFullHomePageContent(storeId);

                // Ensure aboutUsSection exists
                if (!content.aboutUsSection) {
                    content.aboutUsSection = { imageUrl: "" };
                }

                setHomePageData(content);
                setMediaPreview(content.heroSection.mediaUrl);
                setAboutUsImagePreview(content.aboutUsSection?.imageUrl || "");
            } catch (err) {
                console.error("Error fetching home page content:", err);
                setError(t('failedToLoadHomePageContent'));
            } finally {
                setLoading(false);
            }
        };

        fetchHomePageContent();
    }, [storeId]);

    // Handle form input changes for multilingual fields
    const handleLanguageChange = (section, field, value, language) => {
        setHomePageData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [language]: {
                    ...prev[section][language],
                    [field]: value
                }
            }
        }));
    };

    // Handle form input changes for non-language specific fields
    const handleChange = (section, field, value) => {
        setHomePageData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    // Handle media file upload
    const handleMediaChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');

        if (!isVideo && !isImage) {
            setError(t('selectValidImageOrVideo'));
            return;
        }

        // Update media type based on file
        const mediaType = isVideo ? 'video' : 'image';
        handleChange('heroSection', 'mediaType', mediaType);

        // Preview the selected media
        const reader = new FileReader();
        reader.onload = () => {
            setMediaPreview(reader.result);
        };
        reader.readAsDataURL(file);

        setMediaFile(file);
    };

    // Handle media removal
    const handleRemoveMedia = () => {
        if (window.confirm(t('confirmRemoveMedia'))) {
            setMediaPreview("");
            setMediaFile(null);
            handleChange('heroSection', 'mediaUrl', '');
            handleChange('heroSection', 'mediaType', 'video');
        }
    };

    // Handle About Us image upload
    const handleAboutUsImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type (only images)
        if (!file.type.startsWith('image/')) {
            setError(t('selectValidImage') || 'Please select a valid image file');
            return;
        }

        // Preview the selected image
        const reader = new FileReader();
        reader.onload = () => {
            setAboutUsImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        setAboutUsImageFile(file);
    };

    // Handle About Us image removal
    const handleRemoveAboutUsImage = () => {
        if (window.confirm(t('confirmRemoveImage') || 'Are you sure you want to remove this image?')) {
            setAboutUsImagePreview("");
            setAboutUsImageFile(null);
            handleChange('aboutUsSection', 'imageUrl', '');
        }
    };

    // Save home page content
    const handleSave = async () => {
        if (!storeId) {
            setError(t('storeIdRequired'));
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            let dataToSave = { ...homePageData };

            // Upload new media file if selected
            if (mediaFile) {
                const fileExtension = mediaFile.name.split('.').pop();
                const fileName = `homepage/hero-${Date.now()}.${fileExtension}`;
                const mediaUrl = await uploadFile(storeId, fileName, mediaFile);
                dataToSave.heroSection.mediaUrl = mediaUrl;
            }

            // Upload new About Us image if selected
            if (aboutUsImageFile) {
                const fileExtension = aboutUsImageFile.name.split('.').pop();
                const fileName = `homepage/aboutus-${Date.now()}.${fileExtension}`;
                const aboutUsImageUrl = await uploadFile(storeId, fileName, aboutUsImageFile);

                // Ensure aboutUsSection exists before setting imageUrl
                if (!dataToSave.aboutUsSection) {
                    dataToSave.aboutUsSection = {};
                }
                dataToSave.aboutUsSection.imageUrl = aboutUsImageUrl;
            }

            await updateHomePageContent(storeId, dataToSave);
            setSuccess("Home page content saved successfully!");

            // Update local state with the saved data
            setHomePageData(dataToSave);
            setMediaFile(null);
            setAboutUsImageFile(null);
        } catch (err) {
            console.error("Error saving home page content:", err);
            setError(`Failed to save: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // Clear messages after delay
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>{t('loadingHomePageContent')}</p>
            </div>
        );
    }

    return (
        <div className="homepage-tab">
            <div className="tab-header">
                <h3>{t('homePageContent')}</h3>
                <p>{t('customizeHomePageDescription')}</p>

                {/* Language Switcher */}
                <div className="language-switcher">
                    <label>{t('editContentIn')}</label>
                    <div className="language-tabs">
                        <button
                            className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
                            onClick={() => setActiveLanguage('en')}
                        >
                            🇺{t('english')}
                        </button>
                        <button
                            className={`language-tab ${activeLanguage === 'ar' ? 'active' : ''}`}
                            onClick={() => setActiveLanguage('ar')}
                        >
                            {t('arabic')}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Hero Section Settings */}
            <div className="settings-group">
                <h4>🎬 {t('heroSection')}</h4>
                <p className="group-description">
                    {t('heroSectionDescription')}
                </p>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="hero-title">
                            {t('mainTitle')} ({activeLanguage === 'en' ? t('english') : t('arabic')}) *
                        </label>
                        <input
                            id="hero-title"
                            type="text"
                            value={homePageData.heroSection[activeLanguage]?.title || ""}
                            onChange={(e) => handleLanguageChange('heroSection', 'title', e.target.value, activeLanguage)}
                            placeholder={activeLanguage === 'en' ? t('mainTitlePlaceholder') : t('mainTitlePlaceholder')}
                            maxLength={100}
                            dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                        />
                        <small>{t('displayedAsLargeHeading')}</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="hero-subtitle">
                            {t('subtitle')} ({activeLanguage === 'en' ? t('english') : t('arabic')})
                        </label>
                        <input
                            id="hero-subtitle"
                            type="text"
                            value={homePageData.heroSection[activeLanguage]?.subtitle || ""}
                            onChange={(e) => handleLanguageChange('heroSection', 'subtitle', e.target.value, activeLanguage)}
                            placeholder={activeLanguage === 'en' ? t('subtitlePlaceholder') : t('subtitlePlaceholder')}
                            maxLength={200}
                            dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                        />
                        <small>{t('appearsUnderMainTitle')}</small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="hero-media">{t('heroMediaUpload')}</label>
                        <input
                            id="hero-media"
                            type="file"
                            accept="video/*,image/*"
                            onChange={handleMediaChange}
                        />
                        <small>{t('supportedFormats')}</small>
                    </div>


                </div>

                {mediaPreview && (
                    <div className="media-preview">
                        <div className="media-preview-header">
                            <h5>{t('currentMedia')}</h5>
                            <button
                                type="button"
                                className="remove-media-btn"
                                onClick={handleRemoveMedia}
                                title={t('removeMediaTitle')}
                            >
                                ✕ Remove
                            </button>
                        </div>
                        <div className="media-preview-content">
                            {homePageData.heroSection.mediaType === 'video' ? (
                                <video
                                    src={mediaPreview}
                                    controls
                                    style={{ maxWidth: '300px', maxHeight: '200px' }}
                                />
                            ) : (
                                <img
                                    src={mediaPreview}
                                    alt={t('heroPreview')}
                                    style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'cover' }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Story Section Settings */}
            <div className="settings-group">
                <h4>📖 {t('storySection')}</h4>
                <p className="group-description">
                    {t('storySectionDescription')}
                </p>

                <div className="form-group">
                    <label htmlFor="story-title">
                        {t('storyTitle')} ({activeLanguage === 'en' ? t('english') : t('arabic')}) *
                    </label>
                    <input
                        id="story-title"
                        type="text"
                        value={homePageData.storySection[activeLanguage]?.title || ""}
                        onChange={(e) => handleLanguageChange('storySection', 'title', e.target.value, activeLanguage)}
                        placeholder={activeLanguage === 'en' ? t('storyTitlePlaceholder') : t('storyTitlePlaceholder')}
                        maxLength={100}
                        dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                    />
                    <small>{t('displayedAsLargeHeading')}</small>
                </div>

                <div className="form-group">
                    <label htmlFor="story-description">
                        {t('storyDescription')} ({activeLanguage === 'en' ? t('english') : t('arabic')}) *
                    </label>
                    <textarea
                        id="story-description"
                        value={homePageData.storySection[activeLanguage]?.description || ""}
                        onChange={(e) => handleLanguageChange('storySection', 'description', e.target.value, activeLanguage)}
                        placeholder={activeLanguage === 'en' ? t('storyDescriptionPlaceholder') : t('storyDescriptionPlaceholder')}
                        rows={4}
                        maxLength={500}
                        dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                    />
                    <small>{t('storySectionDescription')}</small>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="story-button-text">
                            Button Text ({activeLanguage === 'en' ? 'English' : 'Arabic'})
                        </label>
                        <input
                            id="story-button-text"
                            type="text"
                            value={homePageData.storySection[activeLanguage]?.buttonText || ""}
                            onChange={(e) => handleLanguageChange('storySection', 'buttonText', e.target.value, activeLanguage)}
                            placeholder={activeLanguage === 'en' ? "e.g., DISCOVER MORE" : "مثال: اكتشف المزيد"}
                            maxLength={30}
                            dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="story-button-link">Button Link</label>
                        <input
                            id="story-button-link"
                            type="text"
                            value={homePageData.storySection.buttonLink}
                            onChange={(e) => handleChange('storySection', 'buttonLink', e.target.value)}
                            placeholder="/shop or /about"
                        />
                        <small>Where the button should take customers (use /shop, /about, etc.)</small>
                    </div>
                </div>
            </div>

            {/* About Us Section Settings */}
            <div className="settings-group">
                <h4>🏢 About Us Section</h4>
                <p className="group-description">
                    Customize the hero image for your About Us page. This image will be displayed at the top of the About Us page.
                </p>

                <div className="form-group">
                    <label htmlFor="aboutus-image">About Us Hero Image</label>
                    <input
                        id="aboutus-image"
                        type="file"
                        accept="image/*"
                        onChange={handleAboutUsImageChange}
                    />
                    <small>Upload an image that represents your business or brand (recommended: 1920x400px)</small>
                </div>

                {aboutUsImagePreview && (
                    <div className="media-preview">
                        <div className="media-preview-header">
                            <h5>Current About Us Image</h5>
                            <button
                                type="button"
                                className="remove-media-btn"
                                onClick={handleRemoveAboutUsImage}
                                title="Remove About Us Image"
                            >
                                ✕ Remove
                            </button>
                        </div>
                        <div className="media-preview-content">
                            <img
                                src={aboutUsImagePreview}
                                alt="About Us Preview"
                                style={{ maxWidth: '400px', maxHeight: '200px', objectFit: 'cover' }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="tab-actions">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="admin-primary-btn"
                >
                    {saving ? t('saving') : t('saveHomePageContent')}
                </button>
            </div>

            <style jsx>{`
                .homepage-tab {
                    padding: 20px 0;
                }

                .tab-header {
                    margin-bottom: 30px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #f0f0f0;
                }

                .tab-header h3 {
                    color: #333;
                    margin-bottom: 5px;
                }

                .language-switcher {
                    margin-top: 20px;
                    padding: 15px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .language-switcher label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 10px;
                    color: #495057;
                }

                .language-tabs {
                    display: flex;
                    gap: 10px;
                }

                .language-tab {
                    padding: 8px 16px;
                    border: 2px solid #dee2e6;
                    background: white;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    font-size: 14px;
                }

                .language-tab:hover {
                    border-color: #007bff;
                    background: #f8f9ff;
                }

                .language-tab.active {
                    border-color: #007bff;
                    background: #007bff;
                    color: white;
                }

                .settings-group {
                    margin-bottom: 40px;
                    padding: 25px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    border-left: 4px solid #007bff;
                }

                .settings-group h4 {
                    color: #333;
                    margin-bottom: 10px;
                    font-size: 1.2em;
                }

                .group-description {
                    color: #666;
                    margin-bottom: 20px;
                    font-style: italic;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 5px;
                    color: #333;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 5px;
                    font-size: 14px;
                }

                .form-group small {
                    display: block;
                    color: #666;
                    margin-top: 5px;
                    font-size: 12px;
                }

                .media-preview {
                    margin-top: 15px;
                    padding: 15px;
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .media-preview-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }

                .media-preview-header h5 {
                    margin: 0;
                    color: #333;
                }

                .remove-media-btn {
                    background: #dc3545;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .remove-media-btn:hover {
                    background: #c82333;
                    transform: scale(1.05);
                }

                .media-preview-content {
                    display: flex;
                    justify-content: center;
                }

                .homepage-preview {
                    border: 2px dashed #ddd;
                    border-radius: 10px;
                    overflow: hidden;
                }

                .preview-hero {
                    height: 200px;
                    background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    text-align: center;
                }

                .preview-overlay {
                    background: rgba(0,0,0,0.3);
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .preview-story {
                    padding: 30px;
                    text-align: center;
                    background: white;
                }

                .preview-story h2 {
                    margin-bottom: 15px;
                    color: #333;
                }

                .preview-story button {
                    margin-top: 15px;
                    padding: 10px 20px;
                    background: #333;
                    color: white;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                }

                .tab-actions {
                    margin-top: 30px;
                    padding-top: 20px;
                    border-top: 1px solid #eee;
                }

                .loading-container {
                    text-align: center;
                    padding: 40px;
                }

                .error-message {
                    background: #f8d7da;
                    color: #721c24;
                    padding: 12px;
                    border-radius: 5px;
                    margin-bottom: 20px;
                }

                .success-message {
                    background: #d4edda;
                    color: #155724;
                    padding: 12px;
                    border-radius: 5px;
                    margin-bottom: 20px;
                }

                @media (max-width: 768px) {
                    .form-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .preview-hero {
                        height: 150px;
                    }
                }
            `}</style>
        </div>
    );
};

export default HomePageTab;
